// ============================================================================
// Créature origami d'une SKILLCARD (migration 0274) — PUR, testé.
// ============================================================================
// La créature n'est JAMAIS une image ni un SVG brut produit par l'IA : c'est une
// DESCRIPTION géométrique (`cards.creature`) — des polygones « plis » dont la
// couleur est une NUANCE relative à la teinte de la carte (-1 sombre … +1
// clair) ou une couleur nommée (yeux, or…). Nous la rendons nous-mêmes en SVG :
//   - aucun risque d'injection (seuls des points et des couleurs validés) ;
//   - la créature se recolore seule quand la teinte change (niveau, bleu nuit).
//
// Forme ÉVOLUÉE = la base + des plis ARRIÈRE (ailes, queue…) et AVANT (couronne,
// ornements dorés), rendus avec une nuance un peu plus claire.
//
// ⚠️ MIROIR Deno : supabase/functions/_shared/card-creature.ts (validation de la
// sortie LLM + choix de l'espèce). Parité testée côté _shared.
// ============================================================================

import { z } from 'zod';

// ── Couleurs nommées ────────────────────────────────────────────────────────

export const CREATURE_NAMED_COLORS = {
  ink: '#1B1530',
  gold: '#F5C542',
  'gold-light': '#FFE08A',
  'gold-deep': '#E8B432',
  blush: '#FF9DB8',
} as const;
export type CreatureNamedColor = keyof typeof CREATURE_NAMED_COLORS;

export const CREATURE_LIMITS = {
  minVertices: 3,
  maxVertices: 8,
  minBase: 4,
  maxBase: 80,
  maxEvolved: 24,
  /** Coordonnées tolérées : viewBox 0..100, avec un peu de débord. */
  minCoord: -10,
  maxCoord: 110,
} as const;

const C = CREATURE_LIMITS;
const NUM = String.raw`-?\d{1,3}(?:\.\d{1,2})?`;
const POINT = `${NUM},${NUM}`;
const POINTS_RE = new RegExp(`^${POINT}(?: ${POINT}){${C.minVertices - 1},${C.maxVertices - 1}}$`);

/** « x,y x,y … » — 3 à 8 sommets, coordonnées dans la viewBox (débord toléré). */
export const creaturePointsSchema = z
  .string()
  .trim()
  .regex(POINTS_RE, 'points : « x,y x,y … » (3 à 8 sommets, 2 décimales max)')
  .refine(
    (s) =>
      s
        .split(/[ ,]/)
        .map(Number)
        .every((n) => n >= C.minCoord && n <= C.maxCoord),
    'coordonnées hors de la viewBox 0..100',
  );

export const creatureFillSchema = z.union([
  z.number().min(-1).max(1),
  z.enum(Object.keys(CREATURE_NAMED_COLORS) as [CreatureNamedColor, ...CreatureNamedColor[]]),
  z.string().regex(/^#[0-9a-fA-F]{6}$/, 'couleur : #rrggbb'),
]);
export type CreatureFill = z.infer<typeof creatureFillSchema>;

export const creaturePolySchema = z.object({
  points: creaturePointsSchema,
  fill: creatureFillSchema,
});
export type CreaturePoly = z.infer<typeof creaturePolySchema>;

export const cardCreatureSchema = z.object({
  species: z.string().trim().min(1).max(40),
  base: z.array(creaturePolySchema).min(C.minBase).max(C.maxBase),
  evolvedBack: z.array(creaturePolySchema).max(C.maxEvolved).default([]),
  evolvedFront: z.array(creaturePolySchema).max(C.maxEvolved).default([]),
});
export type CardCreature = z.infer<typeof cardCreatureSchema>;

/** Spec valide, ou `null` (absente / invalide → la carte affiche la créature par défaut). */
export function parseCardCreature(raw: unknown): CardCreature | null {
  if (raw == null) return null;
  const r = cardCreatureSchema.safeParse(raw);
  return r.success ? r.data : null;
}

// ── Espèce selon le sujet ───────────────────────────────────────────────────

export const CARD_CREATURE_SPECIES = {
  grue: 'grue',
  renard: 'renard',
  koi: 'poisson koï',
  lapin: 'lapin',
  hibou: 'hibou',
  libellule: 'libellule',
  tortue: 'tortue',
  abeille: 'abeille',
} as const;
export type CardCreatureSpecies = keyof typeof CARD_CREATURE_SPECIES;

/** Mots-clés (sans accents, minuscules) → espèce. Le premier groupe qui matche gagne. */
export const CREATURE_SPECIES_KEYWORDS: ReadonlyArray<[CardCreatureSpecies, readonly string[]]> = [
  [
    'grue',
    [
      'geometr',
      'triangle',
      'angle',
      'cercle',
      'aire',
      'perimetre',
      'pythagore',
      'thales',
      'vecteur',
      'trigonometr',
      'symetrie',
      'volume',
      'droite',
      'polygone',
      'repere',
    ],
  ],
  [
    'lapin',
    ['probabilit', 'statisti', 'hasard', 'moyenne', 'mediane', 'denombrement', 'loi binomiale'],
  ],
  [
    'koi',
    [
      'fonction',
      'derive',
      'derivation',
      'integr',
      'primitive',
      'analyse',
      'suite',
      'limite',
      'logarithme',
      'exponentiel',
      'variation',
      'coefficient directeur',
      'affine',
    ],
  ],
  ['abeille', ['algorithm', 'tableur', 'programm', 'python', 'boucle']],
  [
    'renard',
    [
      'algebre',
      'calcul',
      'equation',
      'inequation',
      'puissance',
      'fraction',
      'developper',
      'factoris',
      'identite',
      'distributivite',
      'racine',
      'proportionnal',
      'pourcentage',
      'discriminant',
      'second degre',
      'nombre',
    ],
  ],
  [
    'libellule',
    ['energie', 'electri', 'force', 'vitesse', 'onde', 'lumiere', 'circuit', 'mecanique'],
  ],
  ['tortue', ['atome', 'molecule', 'chimi', 'reaction', 'cellule', 'climat', 'terre']],
  ['hibou', ['raisonnement', 'logique', 'demonstration', 'methode']],
];

function normalize(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Espèce de la créature : d'après le domaine, le sujet et le titre de la carte
 * (géométrie → grue, algèbre → renard, fonctions → koï…), sinon d'après la
 * matière (physique → libellule, sciences → tortue), sinon le hibou.
 */
export function creatureSpeciesFor(input: {
  subject: string;
  domainLabel?: string | null;
  topic?: string | null;
  title?: string | null;
}): CardCreatureSpecies {
  const text = normalize([input.domainLabel, input.topic, input.title].filter(Boolean).join(' '));
  for (const [species, keywords] of CREATURE_SPECIES_KEYWORDS) {
    if (keywords.some((k) => text.includes(k))) return species;
  }
  if (input.subject.startsWith('physique')) return 'libellule';
  if (input.subject === 'sciences' || input.subject === 'enseignement-scientifique')
    return 'tortue';
  return 'hibou';
}

// ── Couleurs ────────────────────────────────────────────────────────────────

function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

/** Éclaircit (s > 0, vers le blanc) ou assombrit (s < 0, vers le noir) une couleur. */
export function shadeHex(hex: string, s: number): string {
  const t = s >= 0 ? 255 : 0;
  const k = Math.abs(s);
  return (
    '#' +
    rgb(hex)
      .map((v) =>
        Math.round(v + (t - v) * k)
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

/** Couleur effective d'un pli. */
export function creatureFillHex(fill: CreatureFill, accent: string, evolved = false): string {
  if (typeof fill === 'number') {
    return shadeHex(accent, Math.max(-0.6, Math.min(0.85, fill + (evolved ? 0.08 : 0))));
  }
  if (fill.startsWith('#')) return fill.toLowerCase();
  return CREATURE_NAMED_COLORS[fill as CreatureNamedColor];
}

export type CreatureLayer = 'evolved-back' | 'base' | 'evolved-front';

export interface CreatureSvgPolygon {
  layer: CreatureLayer;
  points: string;
  fill: string;
  stroke: string;
  /** Nuance d'origine (aller-retour export → import), si relative. */
  shade?: number;
  /** Couleur nommée d'origine, si nommée. */
  named?: CreatureNamedColor;
}

/** Polygones à dessiner, dans l'ordre (arrière → base → avant). */
export function creatureSvgModel(
  spec: CardCreature,
  accent: string,
  evolved: boolean,
): CreatureSvgPolygon[] {
  const stroke = shadeHex(accent, 0.6);
  const map =
    (layer: CreatureLayer) =>
    (p: CreaturePoly): CreatureSvgPolygon => ({
      layer,
      points: p.points,
      fill: creatureFillHex(p.fill, accent, evolved),
      stroke,
      ...(typeof p.fill === 'number' ? { shade: p.fill } : {}),
      ...(typeof p.fill === 'string' && !p.fill.startsWith('#')
        ? { named: p.fill as CreatureNamedColor }
        : {}),
    });
  return [
    ...(evolved ? spec.evolvedBack.map(map('evolved-back')) : []),
    ...spec.base.map(map('base')),
    ...(evolved ? spec.evolvedFront.map(map('evolved-front')) : []),
  ];
}

// ── Export SVG ──────────────────────────────────────────────────────────────

function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * Fichier SVG autonome, éditable (Inkscape, Figma…) et ré-importable : trois
 * calques `<g id="evolved-back|base|evolved-front">`, chaque pli garde sa
 * nuance (`data-shade`) ou sa couleur nommée (`data-color`) pour un aller-retour
 * sans perte tant que sa couleur n'est pas modifiée.
 */
export function creatureToSvg(spec: CardCreature, accent: string): string {
  const polys = creatureSvgModel(spec, accent, true);
  const group = (layer: CreatureLayer) => {
    const items = polys
      .filter((p) => p.layer === layer)
      .map((p) => {
        const data =
          p.shade !== undefined
            ? ` data-shade="${p.shade}"`
            : p.named
              ? ` data-color="${p.named}"`
              : '';
        // Couleurs de la forme de BASE (sans l'éclaircissement « évolué ») : c'est la référence.
        const fill =
          p.shade !== undefined
            ? shadeHex(accent, Math.max(-0.6, Math.min(0.85, p.shade)))
            : p.fill;
        return `    <polygon points="${p.points}" fill="${fill}"${data} stroke="${p.stroke}" stroke-opacity="0.5" stroke-width="0.5" stroke-linejoin="round"/>`;
      });
    return `  <g id="${layer}">\n${items.join('\n')}\n  </g>`;
  };
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="400" height="400" data-accent="${accent}">`,
    `  <title>${escapeXml(spec.species)}</title>`,
    group('evolved-back'),
    group('base'),
    group('evolved-front'),
    '</svg>',
    '',
  ].join('\n');
}

// ── Créature par défaut (le renard de la maquette) ──────────────────────────

export const DEFAULT_CARD_CREATURE: CardCreature = {
  species: 'renard',
  base: [
    { points: '12,12 38,34 18,50', fill: 0.1 },
    { points: '88,12 62,34 82,50', fill: -0.15 },
    { points: '38,34 62,34 50,56', fill: 0 },
    { points: '18,50 38,34 50,56', fill: 0.25 },
    { points: '82,50 62,34 50,56', fill: -0.22 },
    { points: '18,50 50,56 50,88', fill: 0.45 },
    { points: '82,50 50,56 50,88', fill: 0.12 },
    { points: '44,82 56,82 50,91', fill: 'ink' },
    { points: '32,50 43,50 38,57', fill: 'ink' },
    { points: '68,50 57,50 62,57', fill: 'ink' },
  ],
  evolvedBack: [
    { points: '18,50 5,62 24,64', fill: 0.3 },
    { points: '82,50 95,62 76,64', fill: -0.1 },
  ],
  evolvedFront: [
    { points: '50,3 40,32 50,25', fill: 'gold-light' },
    { points: '50,3 60,32 50,25', fill: 'gold-deep' },
    { points: '12,12 20,25 14,27', fill: 'gold' },
    { points: '88,12 80,25 86,27', fill: 'gold' },
  ],
};
